import { failure, isOk, ok, pending, Result } from "@/common/common";
import { StringFieldModel } from "@/common/commonForms";
import { DecisionType } from "@/common/decisions";
import { DocumentType, getDocumentLabel } from "@/common/documents";
import { FeatureToggles } from "@/common/featureToggles";
import { ModuleName } from "@/common/product";
import { PaidSubscription, SubscriptionType } from "@/common/subscription";
import { AiPromptSource } from "@/components-v2/AiPrompt";
import { DocumentPreviewProps } from "@/components-v2/DocumentPreview";
import { DropdownMenuItemType } from "@/components-v2/Dropdown";
import { SelectProps } from "@/components-v2/Select.tsx";
import { TabProps } from "@/components-v2/Tabs";
import {
  categoryMapping,
  categorySubscriptionMapping,
  DecisionCategory,
  DocumentOption,
  DocumentPreview,
  exclusiveDecisionTypes,
  getAllDecisionTypes,
  getCustomDocumentOptions,
  getDecisionActionLabel,
  getDecisionIcon,
  getEnabledDecisionTypes,
  ProjectCompany,
  ProjectDecisionItemDTO,
  ProjectListItem,
  projectTemplateOptions,
} from "@/dbot/common/commonDomain";
import { CustomDocumentType } from "@/dbot/customDocument/customDocumentDomain";
import { showDuplicateProjectDialog } from "@/dbot/duplicateProject/duplicateProjectDomain";
import { ProjectNavigationState } from "@/dbot/project/projectDomain";
import {
  DCEvent,
  navigate,
  showGeneralError,
  showGeneralSuccess,
  thunk,
  updateStore,
} from "@/events/eventsDomain";
import { ProjectDecisionAttachment, UploadedAttachment } from "@/fileUpload/fileUploadDomain";
import { PreppedI18nState } from "@/i18n/i18nDomain";
import { MeetingType } from "@/meetings/meetingsDomain";
import { powerOfAttorneyApi } from "@/overview/powerOfAttorney/powerOfAttorneyDomain.ts";
import { findLocalizedTextOrDefault } from "@/schedule/scheduleDomain.ts";
import { AgenticApiClient } from "@/shareholderRegister/transactions/agentic/agenticApiClient";
import { AgenticTransactionChatModel } from "@/shareholderRegister/transactions/agentic/agenticTransactionChatDomain";
import { PowerOfAttorney, PowerOfAttorneyType } from "@/types/CompanyInfo";
import { ObjectPermission } from "@/types/Permissions";
import { UserCompany } from "@/types/UserInfo";
import { toggleElement } from "@/utils/arrays";
import { toLocalDateISOString } from "@/utils/Dates";
import { adminProject } from "@/utils/navigation";
import { isBlank } from "@/utils/strings";
import { toProjectReference } from "@/utils/UrnReference";
import { compact, defer, isEmpty, last, remove, sortBy, truncate, uniq } from "lodash";
import identity from "lodash/identity";
import { projectApi, SelectedDecision } from "../common/projectApi";
import { orderlessPredicate } from "@/utils/filter";

const poaTypeToDecisionType: Partial<Record<PowerOfAttorneyType, DecisionType>> = {
  [PowerOfAttorneyType.CAPITAL_INCREASE]: DecisionType.increaseShareCapitalPowerOfAttorney,
  [PowerOfAttorneyType.DIVIDEND]: DecisionType.dividendPowerOfAttorney,
  [PowerOfAttorneyType.ACQUIRE_OWN_SHARES]: DecisionType.acquireOwnSharesPowerOfAttorney,
  [PowerOfAttorneyType.CONVERTIBLE_LOAN]: DecisionType.convertibleLoanPowerOfAttorney,
};

export function getRenewalCandidateDecisionTypes(
  powerOfAttorneys: PowerOfAttorney[],
): DecisionType[] {
  return compact(
    powerOfAttorneys
      .filter((p) => p.isRenewalPoaCandidate && !p.dismissedRenewalCandidate)
      .map((p) => poaTypeToDecisionType[p.type]),
  );
}

export type AddProjectParams = {
  projectName?: string;
  decisions?: Array<SelectedDecision>;
  documents?: Array<DocumentType>;
  existingDecisions?: Array<ExistingDecision>;
  customDocumentType?: CustomDocumentType;
  batchCompanies?: string[];
};

type ExistingDecision = ProjectDecisionItemDTO;

export type ExistingDecisionPreview = {
  existingDecision: ExistingDecision;
  previews: Result<DocumentPreview[]>;
  selectedDocumentId?: string;
};

export type ProjectTemplateAccess = {
  enterpriseAccountId: string;
  enterpriseAccountName: string;
  canUseTemplate: boolean;
  canManageTemplates: boolean;
};

export type ProjectListState = {
  checkedBatchDecisions: SelectedDecision[];
  checkedDecisions: SelectedDecision[];
  checkedExistingDecisions: ExistingDecision[];
  createResult: Result | undefined;
  documents: DocumentType[];
  existingDecisionPreview?: ExistingDecisionPreview;
  existingDecisionsCompanyFilter: string | null;
  existingDecisionsTypeFilter: "all" | "freetext" | "automated";
  existingDecisionsFreetextFilter: string;
  existingDecisionsResult?: Result<ExistingDecision[]>;
  fileAgents: Map<File, AgenticTransactionChatModel>;
  fileSources: File[];
  isAskAvailable: boolean;
  isNewProjectDialogOpen?: boolean;
  missingDecisions: boolean;
  orgNo: string;
  companyId: string;
  projectToDelete: ProjectListItem | null;
  projectsResult: Result<ProjectListItem[]>;
  prompt: string;
  promptChanged: boolean;
  promptInputCtrlRef: { focus: () => void } | null;
  renewalCandidateDecisionTypes: DecisionType[];
  requiredDocumentsByDecision: { [decisionType: string]: DocumentType[] };
  selectedCompanies: string[];
  selectedTab: "company-events" | "recent-matters" | "starred-matters" | "incorporate-subsidiaries";
  showAllPortfolioProjects: boolean;
  projectTemplateAccess?: ProjectTemplateAccess;
};

export const toProjectCompanies = (orgNo: string, companies: UserCompany[]): ProjectCompany[] =>
  companies.map((c) => ({
    orgNo: c.organisationNumber,
    name: c.name,
    isMainCompany: c.organisationNumber === orgNo,
  }));

export const initialProjectListState = ({
  orgNo,
  companyId,
}: {
  orgNo: string;
  companyId: string;
}): ProjectListState => ({
  documents: [],
  projectsResult: pending(),
  projectToDelete: null,
  checkedDecisions: [],
  checkedExistingDecisions: [],
  checkedBatchDecisions: [],
  selectedCompanies: [],
  missingDecisions: false,
  createResult: undefined,
  requiredDocumentsByDecision: {},
  prompt: "",
  promptChanged: false,
  promptInputCtrlRef: null,
  selectedTab: "company-events",
  existingDecisionsFreetextFilter: "",
  existingDecisionsCompanyFilter: orgNo,
  existingDecisionsTypeFilter: "freetext",
  fileSources: [],
  fileAgents: new Map(),
  renewalCandidateDecisionTypes: [],
  showAllPortfolioProjects: false,
  isAskAvailable: false,
  orgNo,
  companyId,
});

const updateProjectList = (update: (state: ProjectListState) => void): DCEvent =>
  updateStore({
    projectList: (state) => {
      if (state) {
        update(state);
      }
      return state;
    },
  });

export const getCheckedNewAndExistingDecisions = (state: ProjectListState): SelectedDecision[] => [
  ...state.checkedDecisions,
  ...state.checkedExistingDecisions,
];

const getDocumentsFromDecisions = (state: ProjectListState): DocumentType[] =>
  uniq(
    getCheckedNewAndExistingDecisions(state).flatMap(
      (d) => state.requiredDocumentsByDecision[d.decisionType] ?? [],
    ),
  );

const getInferredDocumentSources = (state: ProjectListState): AiPromptSource[] =>
  getCheckedNewAndExistingDecisions(state).some((d) => d.decisionType === DecisionType.merger)
    ? [
        {
          id: "merger-plan-document",
          label: { key: "dbot.custom-document.type.merger-plan" },
          icon: "file-text",
          removable: false,
        },
      ]
    : [];

const maybeGenerateProjectName = (state: ProjectListState, i18n: PreppedI18nState) => {
  if (state.promptChanged) return;
  state.prompt =
    state.checkedDecisions.length || state.checkedExistingDecisions.length
      ? createProjectName({
          i18n,
          decisions: [...state.checkedDecisions, ...state.checkedExistingDecisions],
          documents: state.documents,
        })
      : "";
};

const focusPromptInput = (state: ProjectListState) => {
  defer(() => state.promptInputCtrlRef?.focus());
};

export const canAddProject = (
  state: ProjectListState,
  {
    decisions = state.checkedDecisions,
    documents = state.documents,
    existingDecisions = state.checkedExistingDecisions,
  }: Partial<AddProjectParams> = {},
): boolean => !isEmpty(decisions) || !isEmpty(documents) || !isEmpty(existingDecisions);

export const getProjectListDecisionsByCategory = (
  state: ProjectListState,
  paidSubscription: PaidSubscription | undefined,
  featureToggles?: FeatureToggles,
): DecisionsByCategory[] =>
  getDecisionsByCategory({
    checkedDecisions: state.checkedDecisions.map((d) => d.decisionType),
    paidSubscription,
    orgNo: state.orgNo,
    renewalCandidateDecisionTypes: state.renewalCandidateDecisionTypes,
    featureToggles,
  });

export const getProjectListDecisionsByCategoryForBatch = (
  state: ProjectListState,
  paidSubscription: PaidSubscription | undefined,
): DecisionsByCategory[] =>
  getDecisionsByCategory({
    checkedDecisions: state.checkedBatchDecisions.map((d) => d.decisionType),
    paidSubscription,
    orgNo: state.orgNo,
    isBatchProject: true,
  });

export const hasOtherPortfolioProjects = (state: ProjectListState): boolean =>
  state.showAllPortfolioProjects &&
  isOk(state.projectsResult) &&
  state.projectsResult.data.some((x) => !x.isMainCompany);

export const containsProjects = (state: ProjectListState): boolean =>
  isOk(state.projectsResult) && state.projectsResult.data.length > 0;

export const hasOwnProjects = (state: ProjectListState): boolean =>
  isOk(state.projectsResult) &&
  state.projectsResult.data.some((x) => x.organizationnumber === state.orgNo);

export const getProjects = (state: ProjectListState): ProjectListItem[] =>
  !isOk(state.projectsResult)
    ? []
    : !state.showAllPortfolioProjects
      ? state.projectsResult.data.filter((x) => x.organizationnumber === state.orgNo)
      : state.projectsResult.data;

export const isProjectListEmpty = (state: ProjectListState): boolean =>
  isEmpty(state.checkedDecisions) &&
  isEmpty(state.checkedExistingDecisions) &&
  isEmpty(state.documents);

export const getExistingDecisions = (
  state: ProjectListState,
  { starred }: { starred?: boolean } = {},
): ExistingDecision[] => {
  const items = isOk(state.existingDecisionsResult) ? state.existingDecisionsResult.data : [];
  return items.filter((x) => !starred || x.isStarred);
};

export const getFilteredExistingDecisions = (
  state: ProjectListState,
  { starred, i18n }: { starred: boolean; i18n: PreppedI18nState },
): ExistingDecision[] => {
  const textPredicate = orderlessPredicate<ExistingDecision>(
    (x) => [
      getSelectedDecisionLabel(i18n, x),
      x.projectName,
      state.existingDecisionsCompanyFilter ? undefined : x.companyName,
    ],
    state.existingDecisionsFreetextFilter,
  );
  const companyPredicate = (x: ExistingDecision) =>
    !state.existingDecisionsCompanyFilter || x.orgNo === state.existingDecisionsCompanyFilter;
  const typePredicate = (x: ExistingDecision) =>
    state.existingDecisionsTypeFilter === "all" ||
    (state.existingDecisionsTypeFilter === "freetext" &&
      x.decisionType === DecisionType.freetext) ||
    (state.existingDecisionsTypeFilter === "automated" && x.decisionType !== DecisionType.freetext);

  return getExistingDecisions(state, { starred }).filter(
    (x, i) => textPredicate(x, i) && companyPredicate(x) && typePredicate(x),
  );
};

export const getExistingDecisionsCompanySelect = (
  state: ProjectListState,
  companies: ProjectCompany[],
): SelectProps<string | null> => {
  const items = isOk(state.existingDecisionsResult) ? state.existingDecisionsResult.data : [];
  const companiesWithItems = new Set(items.map((i) => i.orgNo));
  const companiesMap = new Map(companies.map((c) => [c.orgNo, c]));
  const otherCompanies = sortBy(companies, "name")
    .map((c) => c.orgNo)
    .filter((orgNo) => orgNo !== state.orgNo && companiesWithItems.has(orgNo));

  return {
    items: [state.orgNo, null, ...otherCompanies],
    idFn: identity,
    labelFn: (orgNo: string | null) => {
      if (orgNo === null) {
        return { key: "all-companies" };
      } else if (orgNo === state.orgNo) {
        return { key: "current-company" };
      } else {
        return companiesMap.get(orgNo)?.name ?? { translated: "" };
      }
    },
    sectionFn: (orgNo: string | null) => {
      if (orgNo !== state.orgNo && orgNo !== null) {
        return { key: "other-companies" };
      }
    },
    value: state.existingDecisionsCompanyFilter,
    onSelect: (orgNo: string | null) => setExistingDecisionsCompanyFilter(orgNo),
  };
};

export const getMatterTypeSelect = (
  state: ProjectListState,
): SelectProps<"all" | "freetext" | "automated"> => {
  const items: Array<"all" | "freetext" | "automated"> = ["all", "freetext", "automated"];
  return {
    items,
    idFn: identity,
    labelFn: (item) => {
      switch (item) {
        case "freetext":
          return { key: "dbot.project-list.matter-type.freetext" };
        case "automated":
          return { key: "dbot.project-list.matter-type.automated" };
        default:
          return { key: "dbot.project-list.matter-type.all" };
      }
    },
    value: state.existingDecisionsTypeFilter,
    onSelect: (item) => [
      updateProjectList((s) => {
        s.existingDecisionsTypeFilter = item;
      }),
    ],
  };
};

export const getTabs = (state: ProjectListState): TabProps[] => {
  return compact<TabProps>([
    {
      id: "company-events",
      label: { key: "feature.company-events" },
      isActive: state.selectedTab === "company-events",
      onSelect: setSelectedTab("company-events"),
    },
    {
      id: "recent-matters",
      label: { key: "dbot.project-list.recent-matters" },
      isActive: state.selectedTab === "recent-matters",
      onSelect: setSelectedTab("recent-matters"),
    },
    {
      id: "starred-matters",
      label: { key: "dbot.project-list.starred-matters" },
      isActive: state.selectedTab === "starred-matters",
      onSelect: setSelectedTab("starred-matters"),
    },
    {
      id: "incorporate-subsidiaries",
      label: { key: "dbot.project-list.incorporate-subsidiaries" },
      tag: { label: { key: "dbot.new" }, color: "info", compact: true },
      isActive: state.selectedTab === "incorporate-subsidiaries",
      onSelect: setSelectedTab("incorporate-subsidiaries"),
    },
  ]);
};

export const getPromptSources = (
  state: ProjectListState,
  i18n: PreppedI18nState,
): AiPromptSource[] => [
  ...state.checkedDecisions.map<AiPromptSource>((d) => ({
    id: d.decisionType,
    label: getSelectedDecisionLabel(i18n, d),
    icon: getDecisionIcon(d.decisionType),
    bg: "alt1",
  })),
  ...state.checkedExistingDecisions.map<AiPromptSource>((d) => ({
    id: d.decisionId,
    label: getSelectedDecisionLabel(i18n, d),
    icon: "copy",
    bg: "alt1",
  })),
  ...getInferredDocumentSources(state),
  ...state.documents.map<AiPromptSource>((d) => ({
    id: d,
    label: getDocumentLabel(d),
    icon: "file-text",
  })),
  ...state.fileSources.map<AiPromptSource>((f) => {
    const toolCall = last(state.fileAgents.get(f)?.streamingToolCalls);
    return {
      id: f.name,
      type: "file",
      label: f.name,
      status:
        toolCall?.serverLabel ||
        (toolCall?.labelKey ? { key: toolCall.labelKey, params: toolCall.labelParams } : undefined),
      icon: "file",
      loading: state.fileAgents.get(f)?.isStreaming,
    };
  }),
];

export const removePromptSource = (i18n: PreppedI18nState, source: AiPromptSource): DCEvent[] => [
  updateProjectList((s) => {
    const removedDecision =
      s.checkedDecisions.some((d) => d.decisionType === source.id) ||
      s.checkedExistingDecisions.some((d) => d.decisionId === source.id);
    s.checkedDecisions = s.checkedDecisions.filter((d) => d.decisionType !== source.id);
    s.checkedExistingDecisions = s.checkedExistingDecisions.filter(
      (d) => d.decisionId !== source.id,
    );
    s.documents = s.documents.filter((d) => d !== source.id);
    s.fileSources
      .filter((file) => file.name === source.id)
      .forEach((file) => removeFileSourceFromState(s, file));
    maybeGenerateProjectName(s, i18n);
    if (removedDecision) s.documents = getDocumentsFromDecisions(s);
  }),
];

export const projectPrimaryCompanyName = (companies: ProjectCompany[], orgNo: string): string =>
  companies.find((x) => !x.isMainCompany && x.orgNo === orgNo)?.name ?? "";

export const openCreateNewBatchProjectDialog = (
  type: DecisionType,
  companies: ProjectCompany[],
): DCEvent[] => [
  updateProjectList((s) => {
    s.isNewProjectDialogOpen = !s.isNewProjectDialogOpen;
    s.checkedBatchDecisions = [{ decisionType: type }];
    s.selectedCompanies = companies.map((x) => x.orgNo);
  }),
];

export const isCompanySelected = (state: ProjectListState, orgNo: string): boolean =>
  state.selectedCompanies.includes(orgNo);

export const deselectAllCompanies = (): DCEvent[] => [
  updateProjectList((s) => {
    s.selectedCompanies = [s.orgNo];
  }),
];

export const selectAllCompanies = (companies: ProjectCompany[]): DCEvent[] => [
  updateProjectList((s) => {
    s.selectedCompanies = companies.map((company) => company.orgNo);
  }),
];

export const toggleCompanySelected = (orgNo: string): DCEvent[] => [
  updateProjectList((s) => {
    s.selectedCompanies = toggleElement(s.selectedCompanies, orgNo);
  }),
];

export const isMainCompany = (companies: ProjectCompany[], orgNo: string): boolean =>
  companies.find((x) => x.isMainCompany)?.orgNo === orgNo;

export const toggleShowOtherProjectsSelected = (): DCEvent[] => [
  updateProjectList((s) => {
    s.showAllPortfolioProjects = !s.showAllPortfolioProjects;
  }),
];

export const createBatchProject = (state: ProjectListState, i18n: PreppedI18nState): DCEvent[] =>
  addProjectEvents(state, {
    projectName: createProjectName({ decisions: state.checkedBatchDecisions, i18n }),
    decisions: state.checkedBatchDecisions,
    batchCompanies: state.selectedCompanies,
  });

export const cancelCreateBatchProject = (): DCEvent[] => [
  updateProjectList((s) => {
    s.isNewProjectDialogOpen = false;
  }),
];

export const addProjectOptions = (
  state: ProjectListState,
  i18n: PreppedI18nState,
): DropdownMenuItemType[] => [
  ...[
    DocumentType.notice_board_meeting,
    DocumentType.board_meeting,
    DocumentType.notice_general_meeting,
    DocumentType.general_meeting,
  ]
    .map<DocumentOption>((dt) => ({ type: dt, label: getDocumentLabel(dt) }))
    .concat(getCustomDocumentOptions() ?? [])
    .map<DropdownMenuItemType>((o) => ({
      type: "item",
      label: o.label,
      tag: o.tag,
      onSelect: createProjectWithDocumentEvents(state, o.type, {
        i18n,
        showProjectNameDialog: true,
        customDocumentType: o.options?.customDocumentType,
      }),
    })),
  ...projectTemplateOptions(state.projectTemplateAccess, state.orgNo, state.companyId),
];

export const fetchProjectsEvent = (orgNo: string): DCEvent =>
  projectApi.getProjects(orgNo, {
    onSuccess: (res) => [
      updateProjectList((state) => {
        state.projectsResult = ok(res.data.projects);
        state.requiredDocumentsByDecision = res.data.requiredDocumentsByDecision;
        state.projectTemplateAccess = res.data.templateAccess ?? undefined;
        if (containsProjects(state) && !hasOwnProjects(state)) {
          state.showAllPortfolioProjects = true;
        }
      }),
    ],
    onFailure: () => [
      updateProjectList((state) => {
        state.projectsResult = failure();
      }),
    ],
  });

export const fetchRecentMattersEvents = (): DCEvent[] => [
  updateProjectList((state) => {
    state.existingDecisionsResult = pending();
  }),
  projectApi.getRecentMatters({
    onSuccess: (res) => [
      updateProjectList((state) => {
        state.existingDecisionsResult = ok(res.data.items);
      }),
    ],
    onFailure: () => [
      updateProjectList((state) => {
        state.existingDecisionsResult = failure();
      }),
    ],
  }),
];

export const addProjectEvents = (
  state: ProjectListState,
  params: AddProjectParams = {},
): DCEvent[] => {
  const {
    projectName = state.prompt,
    decisions = state.checkedDecisions,
    documents = state.documents,
    existingDecisions = state.checkedExistingDecisions,
    customDocumentType,
    batchCompanies,
  } = params;
  if (!canAddProject(state, { decisions, documents, existingDecisions })) {
    return [
      updateProjectList((s) => {
        s.missingDecisions = true;
        s.createResult = failure();
      }),
    ];
  }
  return [
    updateProjectList((s) => {
      s.createResult = pending();
    }),
    projectApi.createProject(
      {
        orgNo: state.orgNo,
        projectName,
        decisions,
        documents,
        customDocumentType,
        decisionReferences: existingDecisions,
        batchCompanies,
      },
      {
        onSuccess: (res) => [
          updateProjectList((s) => {
            s.createResult = ok();
          }),
          navigate(adminProject({ orgNo: state.orgNo, projectId: res.data.projectId }), {
            state: { type: "project-navigation-state", from: "create-new" },
          }),
        ],
        onFailure: () => [
          showGeneralError(),
          updateProjectList((s) => {
            s.createResult = failure();
          }),
        ],
      },
    ),
  ];
};

export const createProjectWithDocumentEvents = (
  state: ProjectListState,
  document: DocumentType,
  {
    i18n,
    showProjectNameDialog,
    customDocumentType,
  }: {
    i18n: PreppedI18nState;
    showProjectNameDialog?: boolean;
    customDocumentType?: CustomDocumentType;
  },
): DCEvent[] => {
  const addProjectParams: AddProjectParams = {
    projectName: createProjectName({ documents: [document], i18n }),
    decisions: [],
    documents: [document],
    customDocumentType,
  };
  return showProjectNameDialog
    ? openAddProjectNameDialogEvents(state, addProjectParams)
    : addProjectEvents(state, addProjectParams);
};

export const openAddProjectNameDialogEvents = (
  state: ProjectListState,
  params: AddProjectParams,
): DCEvent[] => [
  {
    type: "editProjectName",
    name: new StringFieldModel({ value: params.projectName, required: true }),
    save: (projectName: string) => addProjectEvents(state, { ...params, projectName }),
  },
];

export const checkAskAvailable = (agenticApiClient: AgenticApiClient): DCEvent[] => [
  thunk((dispatch) => {
    agenticApiClient.isAskAvailable(new AbortController().signal).then((val) => {
      dispatch([
        updateProjectList((s) => {
          s.isAskAvailable = val;
        }),
      ]);
    });
  }),
];

export const fetchPowerOfAttorney = (state: ProjectListState): DCEvent =>
  powerOfAttorneyApi.fetchPowerOfAttorney({
    orgNo: state.orgNo,
    onSuccess: (res) => [
      updateProjectList((s) => {
        s.renewalCandidateDecisionTypes = getRenewalCandidateDecisionTypes(res.data);
      }),
    ],
    onFailure: () => [],
  });

export const openProject = (
  { id, organizationnumber }: { id: string; organizationnumber: string },
  options?: { state?: ProjectNavigationState; newWindow?: boolean },
): DCEvent[] => [navigate(adminProject({ orgNo: organizationnumber, projectId: id }), options)];

export const selectForDelete = (project: ProjectListItem | null): DCEvent[] => [
  updateProjectList((s) => {
    s.projectToDelete = project;
  }),
];

export const openChangePermissionDialog = (project: ProjectListItem): DCEvent[] => [
  {
    type: "showChangePermissionDialog",
    title: project.projectName,
    objectId: toProjectReference(project.id),
    moduleName: ModuleName.DBOT,
    onUpdate: (permissions: ObjectPermission[]) =>
      (project.numberOfPermissions = permissions.length),
  },
];

export const openDuplicateProjectDialog = (
  state: ProjectListState,
  companies: ProjectCompany[],
  project: ProjectListItem,
  projectName: string,
): DCEvent[] =>
  project.containsNoticeOrMinutesBoardMeetingDocument &&
  !project.containsNoticeOrMinutesGeneralMeetingDocument
    ? [
        {
          type: "new-meeting",
          orgNo: state.orgNo,
          meetingType: MeetingType.BOARD_MEETING,
          projectName: { translated: projectName },
          projectId: project.id,
        },
      ]
    : !project.containsNoticeOrMinutesBoardMeetingDocument &&
        project.containsNoticeOrMinutesGeneralMeetingDocument
      ? [
          {
            type: "new-meeting",
            orgNo: state.orgNo,
            meetingType: MeetingType.GENERAL_MEETING,
            projectName: { translated: projectName },
            projectId: project.id,
          },
        ]
      : showDuplicateProjectDialog({
          projectId: project.id,
          projectName,
          companies,
        });

export const toggleDecision = (i18n: PreppedI18nState, decision: DecisionType): DCEvent[] => [
  updateProjectList((s) => {
    const checked = !s.checkedDecisions.some((d) => d.decisionType === decision);
    s.checkedDecisions = !checked
      ? s.checkedDecisions.filter((d) => d.decisionType !== decision)
      : [...s.checkedDecisions, { decisionType: decision }];
    maybeGenerateProjectName(s, i18n);
    s.documents = getDocumentsFromDecisions(s);
    if (checked) focusPromptInput(s);
  }),
];

export const toggleBatchDecision = (decision: DecisionType): DCEvent[] => [
  updateProjectList((s) => {
    s.checkedBatchDecisions = s.checkedBatchDecisions.some((d) => d.decisionType === decision)
      ? s.checkedBatchDecisions.filter((d) => d.decisionType !== decision)
      : [...s.checkedBatchDecisions, { decisionType: decision }];
  }),
];

export const setSelectedTab = (
  tab: "company-events" | "recent-matters" | "starred-matters" | "incorporate-subsidiaries",
): DCEvent[] => [
  updateProjectList((s) => {
    s.selectedTab = tab;
  }),
  ...closeExistingDecisionPreview(),
];

export const setExistingDecisionsFilter = (filter: string): DCEvent[] => [
  updateProjectList((s) => {
    s.existingDecisionsFreetextFilter = filter;
  }),
];

export const setExistingDecisionsCompanyFilter = (orgNo: string | null): DCEvent[] => [
  updateProjectList((s) => {
    s.existingDecisionsCompanyFilter = orgNo;
  }),
];

export const showAll = (): DCEvent[] => [
  updateProjectList((s) => {
    s.existingDecisionsFreetextFilter = "";
    s.existingDecisionsCompanyFilter = null;
    s.existingDecisionsTypeFilter = "all";
  }),
];

export const isShowingAll = (state: ProjectListState): boolean =>
  state.existingDecisionsFreetextFilter.trim() === "" &&
  state.existingDecisionsCompanyFilter === null &&
  state.existingDecisionsTypeFilter === "all";

export const toggleExistingDecision = (
  i18n: PreppedI18nState,
  d: ExistingDecision,
  checked?: boolean,
): DCEvent[] => [
  updateProjectList((s) => {
    const existingIndex = s.checkedExistingDecisions.findIndex(
      (item) => item.projectId === d.projectId && item.decisionId === d.decisionId,
    );
    const nextChecked = checked ?? existingIndex < 0;
    if (nextChecked && existingIndex < 0) s.checkedExistingDecisions.push(d);
    if (!nextChecked && existingIndex >= 0) s.checkedExistingDecisions.splice(existingIndex, 1);
    maybeGenerateProjectName(s, i18n);
    s.documents = getDocumentsFromDecisions(s);
    if (nextChecked) focusPromptInput(s);
  }),
];

export const toggleExistingDecisionPreview = (
  d: ExistingDecision,
  existingDecisionPreview: ExistingDecisionPreview | undefined,
): DCEvent[] =>
  existingDecisionPreview?.existingDecision.decisionId === d.decisionId
    ? [
        updateProjectList((s) => {
          s.existingDecisionPreview = undefined;
        }),
      ]
    : [
        updateProjectList((s) => {
          s.existingDecisionPreview = {
            existingDecision: d,
            previews: pending(),
          };
        }),
        projectApi.getRecentMatterDetails(d.projectId, d.decisionId, {
          onSuccess: (res) => [
            updateProjectList((s) => {
              if (s.existingDecisionPreview?.existingDecision.decisionId === d.decisionId) {
                const documentTypes = Object.values(DocumentType);
                const previews = sortBy(res.data.previews, (p) =>
                  documentTypes.indexOf(p.documentType),
                );
                s.existingDecisionPreview.previews = ok(previews);
                s.existingDecisionPreview.selectedDocumentId = previews[0]?.documentId;
              }
            }),
          ],
          onFailure: () => [
            showGeneralError(),
            updateProjectList((s) => {
              if (s.existingDecisionPreview?.existingDecision.decisionId === d.decisionId) {
                s.existingDecisionPreview.previews = failure();
              }
            }),
          ],
        }),
      ];

export const closeExistingDecisionPreview = (): DCEvent[] => [
  updateProjectList((s) => {
    s.existingDecisionPreview = undefined;
  }),
];

export const toggleDocument = (document: DocumentType): DCEvent[] => [
  updateProjectList((s) => {
    s.documents = toggleElement(s.documents, document);
  }),
];

export const toggleDecisionStarred = (d: ExistingDecision): DCEvent[] => {
  const starred = !d.isStarred;
  return compact<DCEvent>([
    updateProjectList((s) => {
      const item = getExistingDecisions(s).find((x) => x.decisionId === d.decisionId);
      if (item) {
        item.isStarred = starred;
      }
    }),
    projectApi.starDecision(
      { projectId: d.projectId, decisionId: d.decisionId, starred },
      {
        onSuccess: () => [],
        onFailure: () => [
          showGeneralError(),
          updateProjectList((s) => {
            const item = (
              isOk(s.existingDecisionsResult) ? s.existingDecisionsResult.data : []
            ).find((x) => x.decisionId === d.decisionId);
            if (item) item.isStarred = !starred;
          }),
        ],
      },
    ),
  ]);
};

export const deleteSubmit = (state: ProjectListState): DCEvent[] =>
  state.projectToDelete
    ? [
        projectApi.deleteProject(state.projectToDelete.id, {
          onSuccess: () => [
            fetchProjectsEvent(state.orgNo),
            showGeneralSuccess(),
            updateProjectList((s) => {
              s.projectToDelete = null;
            }),
          ],
          onFailure: () => [showGeneralError()],
        }),
      ]
    : [];

export const setPrompt = (prompt: string): DCEvent[] => [
  updateProjectList((s) => {
    s.prompt = prompt;
    s.promptChanged = !isBlank(prompt);
  }),
];

export const setPromptInputCtrlRef = (ref: { focus: () => void } | null): DCEvent[] => [
  updateProjectList((s) => {
    s.promptInputCtrlRef = ref;
  }),
];
//
// export const addFileSources = (
//   agenticApiClient: AgenticApiClient,
//   i18n: PreppedI18nState,
//   files: File[],
// ): DCEvent[] => files.flatMap((file) => addFileSource(agenticApiClient, i18n, file));
//
// export const addFileSource = (
//   agenticApiClient: AgenticApiClient,
//   i18n: PreppedI18nState,
//   file: File,
// ): DCEvent[] => [
//   thunk((dispatch) => {
//     const agent = new AgenticTransactionChatModel(
//       agenticApiClient,
//       { decisions: true },
//       {
//         onDecisionSuggestions: (suggestions) => {
//           dispatch([
//             updateProjectList((s) => {
//               s.checkedDecisions = uniqBy(
//                 [
//                   ...s.checkedDecisions,
//                   ...(suggestions.decisions?.map<SelectedDecision>((d) => ({
//                     decisionType: d.decisionType,
//                     titles:
//                       d.decisionType === DecisionType.freetext
//                         ? [{ language: i18n.lang, text: d.title ?? "" }]
//                         : undefined,
//                   })) ?? []),
//                 ],
//                 (d) =>
//                   d.decisionType === DecisionType.freetext
//                     ? `freetext-${getSelectedDecisionLabel(i18n, d)}`
//                     : d.decisionType,
//               );
//               s.documents = uniq([
//                 ...s.documents,
//                 ...getDocumentsFromDecisions(s),
//                 ...(suggestions.documents?.map((d) => d.documentType) ?? []),
//               ]);
//               maybeGenerateProjectName(s, i18n);
//               removeFileSourceFromState(s, file);
//             }),
//           ]);
//         },
//       },
//     );
//     dispatch([
//       updateProjectList((s) => {
//         s.fileSources.push(file);
//         s.fileAgents.set(file, agent);
//       }),
//     ]);
//     agent.selectFiles([file], agenticUploadConfig).then(() => agent.sendMessage());
//   }),
// ];

// export const removeFileSource = (file: File): DCEvent[] => [
//   updateProjectList((s) => removeFileSourceFromState(s, file)),
// ];

export const clearProjectListSelection = (): DCEvent[] => [
  updateProjectList((s) => {
    s.checkedDecisions = [];
    s.checkedExistingDecisions = [];
    s.documents = [];
    [...s.fileSources].forEach((file) => removeFileSourceFromState(s, file));
    s.fileSources = [];
    s.prompt = "";
    s.promptChanged = false;
  }),
];

const removeFileSourceFromState = (state: ProjectListState, file: File) => {
  remove(state.fileSources, file);
  state.fileAgents.get(file)?.abort();
  state.fileAgents.delete(file);
};

export const createProjectName = ({
  decisions,
  documents,
  i18n,
}: {
  decisions?: SelectedDecision[];
  documents?: DocumentType[];
  i18n: PreppedI18nState;
}): string => {
  const date = toLocalDateISOString(Date.now());

  if (decisions?.length) {
    return [
      date,
      truncate(decisions.map((d) => getSelectedDecisionLabel(i18n, d)).join(", "), {
        length: 150,
      }),
    ].join(" ");
  }

  if (documents?.length === 1 && documents[0] === DocumentType.custom_document) {
    return i18n.t("dbot.custom-document.untitled-document");
  }

  if (documents?.length) {
    return [date, i18n.t(getDocumentLabel(documents[0]))].join(" ");
  }

  return date;
};

export const groupDecisionTypesByCategory = (
  decisionTypes: DecisionType[],
): {
  category: DecisionCategory;
  requiredSubscriptionType: SubscriptionType;
  decisions: DecisionType[];
}[] => {
  return Object.values(DecisionCategory)
    .map((category) => ({
      category: category,
      requiredSubscriptionType: categorySubscriptionMapping[category],
      decisions: Object.entries(categoryMapping)
        .filter(([t, c]) => c === category && decisionTypes.includes(t as DecisionType))
        .map(([t]) => t as DecisionType),
    }))
    .filter(({ decisions }) => decisions.length > 0);
};

export type DecisionsByCategory = {
  category: DecisionCategory;
  decisions: {
    decisionType: DecisionType;
    isSelected: boolean;
    isDisabled: boolean;
    isBlockedByExclusiveDecision: boolean;
    isExisting?: boolean;
    isRenewalCandidate?: boolean;
  }[];
  requiredSubscriptionType: SubscriptionType;
  currentSubscription: PaidSubscription | undefined;
};

export const getDecisionsByCategory = ({
  checkedDecisions,
  paidSubscription,
  isBatchProject,
  orgNo,
  current,
  renewalCandidateDecisionTypes,
  featureToggles,
}: {
  checkedDecisions: DecisionType[];
  paidSubscription: PaidSubscription | undefined;
  isBatchProject?: boolean;
  orgNo?: string;
  current?: DecisionType[];
  renewalCandidateDecisionTypes?: DecisionType[];
  featureToggles?: FeatureToggles;
}): DecisionsByCategory[] => {
  current = current || [];
  const decisionTypes = getAllDecisionTypes(orgNo);
  const enabledDecisionTypes = getEnabledDecisionTypes({ isBatchProject, featureToggles });
  const selectedDecisionTypes = uniq([...checkedDecisions, ...current]);
  const hasExclusiveSelected = selectedDecisionTypes.some((d) => exclusiveDecisionTypes.has(d));
  return groupDecisionTypesByCategory(decisionTypes)
    .map(({ category, decisions, requiredSubscriptionType }) => ({
      category,
      decisions: decisions
        .filter((decisionType) => enabledDecisionTypes.includes(decisionType))
        .map((decisionType) => {
          const isSelected = checkedDecisions.includes(decisionType);
          const isExisting = current.includes(decisionType);
          return {
            decisionType,
            isSelected,
            isDisabled: !enabledDecisionTypes.includes(decisionType),
            isExisting,
            isRenewalCandidate: renewalCandidateDecisionTypes?.includes(decisionType) ?? false,
            isBlockedByExclusiveDecision:
              !isSelected &&
              !isExisting &&
              (exclusiveDecisionTypes.has(decisionType)
                ? selectedDecisionTypes.some((d) => d !== decisionType)
                : hasExclusiveSelected),
          };
        }),
      currentSubscription: paidSubscription,
      requiredSubscriptionType,
    }))
    .filter(({ decisions }) => decisions.length > 0);
};

export function getSelectedDecisionLabel(i18n: PreppedI18nState, d: SelectedDecision): string {
  return i18n.tf(
    findLocalizedTextOrDefault(i18n, d.titles) ?? getDecisionActionLabel(d.decisionType),
  );
}

export function getExistingDecisionPreviewTabs(
  existingDecisionPreview: ExistingDecisionPreview,
  documentPreviews: DocumentPreview[],
): TabProps[] {
  return documentPreviews.map((p) => ({
    id: p.documentId,
    label: getDocumentLabel(p.documentType),
    isActive: existingDecisionPreview.selectedDocumentId === p.documentId,
    onSelect: [
      updateProjectList((s) => {
        if (s.existingDecisionPreview) {
          s.existingDecisionPreview.selectedDocumentId = p.documentId;
        }
      }),
    ],
  }));
}

export function getExistingDecisionPreviewContent(
  existingDecisionPreview: ExistingDecisionPreview,
): DocumentPreviewProps["content"] {
  const preview = isOk(existingDecisionPreview.previews)
    ? existingDecisionPreview.previews.data.find(
        (d) => d.documentId === existingDecisionPreview.selectedDocumentId,
      )
    : undefined;
  if (existingDecisionPreview.selectedDocumentId && preview) {
    return [{ type: "document", ...preview }];
  }
}

function toUploadedAttachment(
  d: ExistingDecision,
  a: ProjectDecisionAttachment,
): UploadedAttachment {
  return {
    id: a.id,
    filename: a.filename,
    downloadUrl: projectApi.downloadAttachmentUrl({
      projectId: d.projectId,
      attachmentId: a.id,
    }),
  };
}

export function getExistionDecisionAttachments(
  d: ExistingDecision,
  existingDecisionPreview: ExistingDecisionPreview,
): UploadedAttachment[] | undefined {
  const preview = isOk(existingDecisionPreview?.previews)
    ? existingDecisionPreview.previews.data.find(
        (d) => d.documentId === existingDecisionPreview.selectedDocumentId,
      )
    : undefined;
  const filteredAttachments =
    existingDecisionPreview.selectedDocumentId && preview
      ? d.attachments?.filter((a) => a.selectedDocuments.includes(preview.documentType))
      : d.attachments;
  return filteredAttachments?.map<UploadedAttachment>((a) => toUploadedAttachment(d, a));
}

export function openAttachment(d: ExistingDecision, attachmentId: string): DCEvent[] {
  const a = d.attachments?.find((x) => x.id === attachmentId);
  if (!a) {
    return [];
  }
  return [
    {
      type: "preview-file",
      filename: a.filename,
      file: {
        url: projectApi.downloadAttachmentUrl({ projectId: d.projectId, attachmentId: a.id }),
      },
    },
  ];
}
